import type { PaletteMode } from "../constants/legoColors";
import { EXAMPLE_IMAGES } from "../data/exampleImages";

// Shareable conversion state carried in the URL hash. Uploaded images are
// data URLs (too large / private) — only example-based mosaics are fully
// shareable; otherwise the link restores settings alone.
export interface ShareState {
  pixelSize: number;
  paletteMode: PaletteMode;
  dither: boolean;
  mergeRareColors: boolean;
  depth3d?: boolean;
  exampleId?: string;
}

const MIN_PIXEL_SIZE = 8;
const MAX_PIXEL_SIZE = 32;
const PALETTE_MODE_VALUES: PaletteMode[] = [
  "full",
  "grayscale",
  "classic",
];

export const encodeShareState = (state: ShareState): string => {
  const params = new URLSearchParams();
  params.set("p", String(state.pixelSize));
  params.set("pal", state.paletteMode);
  params.set("d", state.dither ? "1" : "0");
  params.set("m", state.mergeRareColors ? "1" : "0");
  if (state.exampleId) params.set("ex", state.exampleId);
  // `z` rides only when on — absent means off, so older links stay valid
  if (state.depth3d) params.set("z", "1");
  return `#${params.toString()}`;
};

export const parseShareState = (hash: string): ShareState | null => {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!raw) return null;

  const params = new URLSearchParams(raw);
  const pixelSize = Number(params.get("p"));
  const paletteMode = params.get("pal");
  const dither = params.get("d");
  const mergeRareColors = params.get("m");
  const depth3d = params.get("z");
  const exampleId = params.get("ex");

  if (
    !Number.isInteger(pixelSize) ||
    pixelSize < MIN_PIXEL_SIZE ||
    pixelSize > MAX_PIXEL_SIZE
  ) {
    return null;
  }
  if (
    !paletteMode ||
    !PALETTE_MODE_VALUES.includes(paletteMode as PaletteMode)
  ) {
    return null;
  }
  if (dither !== "0" && dither !== "1") return null;
  // `m` was added after `d` — tolerate its absence so older shared links
  // keep working (absent = off)
  if (mergeRareColors !== null && mergeRareColors !== "0" && mergeRareColors !== "1") {
    return null;
  }
  if (depth3d !== null && depth3d !== "0" && depth3d !== "1") {
    return null;
  }
  if (exampleId && !EXAMPLE_IMAGES.some((image) => image.id === exampleId)) {
    return null;
  }

  return {
    pixelSize,
    paletteMode: paletteMode as PaletteMode,
    dither: dither === "1",
    mergeRareColors: mergeRareColors === "1",
    depth3d: depth3d === "1",
    exampleId: exampleId ?? undefined,
  };
};

export const writeShareHash = (state: ShareState): void => {
  window.history.replaceState(
    null,
    "",
    `${window.location.pathname}${window.location.search}${encodeShareState(state)}`,
  );
};

export const clearShareHash = (): void => {
  if (!window.location.hash) return;
  window.history.replaceState(
    null,
    "",
    `${window.location.pathname}${window.location.search}`,
  );
};
